import React, { useEffect, useState } from "react";
import config from "../../../../config.js";
import PropTypes from "prop-types";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { sortBy, filter } from "lodash";
import { format } from "date-fns";

// Webpack is configured to grab SCSS files and compile them so this is just a way of grabbing the scss file and naming it in webpack.
import(/* webpackChunkName: "top-level-category" */ "./topLevelCategory.scss");

const TopLevelCategory = ({
  featureFlags,
  loadProducts,
  loadPromotions,
  profileData,
  productsStatus,
  productsData,
  promotionsData,
  promotionsStatus,
}) => {
  const [validData, setValidData] = useState([]);
  const [estoreName, setEstoreName] = useState("standard");
  // useEffect runs when a component first mounts, or when related data changes
  useEffect(() => {
    if (loadProducts) {
      loadProducts("");
    }
  }, [loadProducts]);

  useEffect(() => {
    if (featureFlags.Promotions && loadPromotions) {
      loadPromotions();
    }
  }, [loadPromotions]);

  useEffect(() => {
    if (profileData) {
      if (profileData.IsSpecsavers) {
        setEstoreName("specsavers");
      } else if (profileData.IsTriton) {
        setEstoreName("triton");
      } else {
        setEstoreName("standard");
      }
    }
  }, [profileData]);

  // Checks if Product Data (Top level categories) have been loaded then filters out null data and any products with no Type set
  useEffect(() => {
    if (productsData) {
      const validProducts = filter(
        productsData,
        (product) => product != null && product.Type,
      );

      // console.log("Valid Products: ", validProducts);
      // Set's the valid Product data to a local state. This is only accessable in this file, defined at the start of the component
      // and can be updated by using setValidData and accessed using validData.
      setValidData(
        featureFlags.AdvancedBionics
          ? validProducts
          : validProducts.filter(
              (product) => !String(product.Code || "").startsWith("AB_"),
            ),
      );
    }
  }, [productsData]);

  const organisationPromotions = (promotionsData || []).filter(
    (promotion) =>
      profileData &&
      Number(promotion.OrganisationCode) === Number(profileData.OrganisationCode)
  ).sort(
    (firstPromotion, secondPromotion) =>
      new Date(firstPromotion.EndDate) - new Date(secondPromotion.EndDate)
  );

  const renderPromotionSummaries = () => (
    <div className="home-promotions">
      {organisationPromotions.map((promotion) => (
        <div className="home-promotion" key={promotion.PromotionId}>
          <h2>{promotion.PromotionName}</h2>
          <div className="promotion-summary-details">
            <p>
              Until {format(new Date(promotion.EndDate), "do MMMM yyyy")}
            </p>
            <Link
              to={`/promotions/${promotion.PromotionId}`}
              className="btn promotion-details-button"
            >
              Find out more
            </Link>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Col>
      <div className="top-level-category-main-div">
        <div className="top-level-category-content">
          {/* Sets home page text content based on config set */}
          {config.siteName && config.siteName === "PHONAK" && (
            <div>
              <div className="row align-items-start">
                <div className="col-lg-6 col-md-12">
                  <h1>Welcome to the Phonak NZ eStore!</h1>
                  {featureFlags.Promotions && renderPromotionSummaries()}
                </div>
                <div className="col-lg-6 col-md-12 text-center">
                  {profileData && (
                    <img
                      src={`/assets/bannerimages/${estoreName}-banner.png?v=${new Date().getTime()}`}
                      className="img-fluid"
                      alt="Banner"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {config.siteName && config.siteName === "UNITRON" && (
            <div>
              <div className="row align-items-start">
                <div className="col-lg-6 col-md-12">
                  <h1>Welcome to the Unitron NZ eStore!</h1>
                  {featureFlags.Promotions && renderPromotionSummaries()}
                </div>
                <div className="col-lg-6 col-md-12 text-center">
                  {profileData && (
                    <img
                      src={`/assets/bannerimages/${estoreName}-banner.png?v=${new Date().getTime()}`}
                      className="img-fluid"
                      alt="Banner"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <Row className="top-level-category-cards">
          {validData &&
            validData.length > 0 &&
            validData.map((product, index) => {
              // loops through validData, that was set earlier, and displays cards that link to each category's main page.
              // NOTE: product is actually a category.
              return (
                <Col
                  xs={12}
                  md={6}
                  xl={4}
                  key={index}
                  className={`${index % 3 == 2 ? "" : "card-right-border"} my-3`}
                >
                  <Link
                    to={
                      product.Name == "BTE/RICs" ||
                      product.Name == "ITEs" ||
                      product.Name == "Stock Orders"
                        ? `/store/${product.Code}`
                        : product.Name == "Custom Hearing Protection"
                          ? `/store/${product.Code}/${product.InverseParentCodeNavigation[0].Code}`
                          : product.Code == "Earpiece"
                            ? `/store/${product.Code}/${product.InverseParentCodeNavigation[0].Code}`
                            : `/store/${product.Code}/${product.Type}`
                    }
                  >
                    <div className="top-level-card">
                      <div className="top-level-img">
                        {/* <span className="helper"></span> */}
                        <img src={product.ImagePath} />
                      </div>
                      <div>
                        <h2 className="top-level-category-title">
                          {product.Name}
                        </h2>
                        <p className="btn">Order Now</p>
                      </div>
                    </div>
                  </Link>
                </Col>
              );
            })}
        </Row>
      </div>
    </Col>
  );
};

//Defining types of props (similar to typescript) to prevent developer from using variables in another way and causing errors.
TopLevelCategory.propTypes = {
  loadProducts: PropTypes.func,
  productsStatus: PropTypes.string,
  productsData: PropTypes.array,
  loadPromotions: PropTypes.func,
  promotionsData: PropTypes.array,
  promotionsStatus: PropTypes.string,
};

export default TopLevelCategory;
